import { useState } from "react";
import { toast } from "sonner";
import { apiError } from "@/lib/api";
import { useTeams, useCreateTeam, useDeleteTeam, useAddTeamMember, useRemoveTeamMember } from "@/hooks/useOrgLibrary";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Users2, Plus, Trash2, X } from "lucide-react";

export function TeamsManager({ members = [] }) {
  const { data: teams = [] } = useTeams();
  const [newName, setNewName] = useState("");
  const memberName = (id) => members.find((m) => m.id === id)?.name || "Unknown";

  const create = useCreateTeam({ onSuccess: () => { toast.success("Team created"); setNewName(""); }, onError: (e) => toast.error(apiError(e)) });
  const del = useDeleteTeam({ onSuccess: () => toast.success("Team deleted"), onError: (e) => toast.error(apiError(e)) });
  const addM = useAddTeamMember({ onError: (e) => toast.error(apiError(e)) });
  const removeM = useRemoveTeamMember({ onError: (e) => toast.error(apiError(e)) });

  return (
    <div className="border border-border bg-card p-6 mb-8" data-testid="teams-manager">
      <div className="flex items-center gap-2 mb-4"><Users2 className="h-5 w-5 text-primary" /><h3 className="font-display font-bold">Teams</h3></div>
      <p className="text-xs text-muted-foreground mb-4">Group members into teams, then share specific courses with them from the Team Library.</p>

      <div className="flex gap-2 mb-6">
        <Input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="e.g. Sales, Engineering" className="rounded-none h-11" data-testid="team-name-input" />
        <Button onClick={() => newName.trim() && create.mutate(newName.trim())} disabled={create.isPending} className="rounded-none h-11 bg-primary hover:opacity-90 gap-1" data-testid="create-team-button">
          <Plus className="h-4 w-4" /> Add team
        </Button>
      </div>

      {teams.length === 0 ? (
        <p className="text-sm text-muted-foreground" data-testid="no-teams">No teams yet.</p>
      ) : (
        <div className="space-y-4" data-testid="teams-list">
          {teams.map((t) => {
            const available = members.filter((m) => !t.member_ids.includes(m.id));
            return (
              <div key={t.id} className="border border-border p-4" data-testid={`team-${t.id}`}>
                <div className="flex items-center justify-between mb-3">
                  <span className="font-semibold">{t.name} <span className="text-xs text-muted-foreground">({t.member_ids.length})</span></span>
                  <button onClick={() => del.mutate(t.id)} className="text-muted-foreground hover:text-destructive" data-testid={`delete-team-${t.id}`}><Trash2 className="h-4 w-4" /></button>
                </div>
                <div className="flex flex-wrap gap-2 mb-3">
                  {t.member_ids.map((mid) => (
                    <span key={mid} className="inline-flex items-center gap-1 bg-secondary px-2 py-1 text-xs" data-testid={`team-member-${t.id}-${mid}`}>
                      {memberName(mid)}
                      <button onClick={() => removeM.mutate({ teamId: t.id, memberId: mid })} data-testid={`remove-team-member-${t.id}-${mid}`}><X className="h-3 w-3" /></button>
                    </span>
                  ))}
                  {t.member_ids.length === 0 && <span className="text-xs text-muted-foreground">No members yet</span>}
                </div>
                {available.length > 0 && (
                  <select
                    className="border border-border h-9 text-sm px-2 bg-background"
                    defaultValue=""
                    onChange={(e) => { if (e.target.value) { addM.mutate({ teamId: t.id, memberId: e.target.value }); e.target.value = ""; } }}
                    data-testid={`add-team-member-${t.id}`}>
                    <option value="" disabled>+ Add member…</option>
                    {available.map((m) => <option key={m.id} value={m.id}>{m.name} ({m.email})</option>)}
                  </select>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
